import React from "react";
import { CheckCircle2, Star, ArrowRight } from "lucide-react";

function PlanHeader({ plan }) {
  return (
    <>
      {plan.highlight && (
        <div className="absolute top-0 right-0 bg-br-bronze text-white text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1.5 flex items-center gap-1" data-testid={`plan-badge-${plan.id}`}>
          <Star size={11} style={{ fill: "#ffffff" }} /> Most Popular
        </div>
      )}
      <div className="p-6">
        <div className="br-eyebrow text-br-bronze mb-2">Membership</div>
        <h3 className="font-display text-2xl tracking-wider uppercase mb-1">{plan.name}</h3>
        {plan.pricePrefix && (
          <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 mt-2">{plan.pricePrefix}</div>
        )}
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-4xl font-bold text-br-black tabular-nums">${plan.price.toLocaleString()}</span>
          <span className="text-xs text-gray-500 uppercase tracking-widest">/ {plan.billing}</span>
        </div>
        <p className="mt-3 text-sm text-gray-700 leading-relaxed">{plan.bestFor}</p>
      </div>
    </>
  );
}

function PlanStats({ plan }) {
  const stats = [
    { label: "Visits", value: plan.visits },
    { label: "Annual Allowance", value: plan.allowance },
    { label: "Included Crew Hours", value: plan.cap },
    { label: "Emergency Access", value: plan.emergency },
  ];
  return (
    <div className="px-6 pb-2 grid grid-cols-2 gap-3 text-xs">
      {stats.map((s) => (
        <div key={s.label} className="bg-br-paper p-3 rounded">
          <div className="text-gray-500 uppercase tracking-wider mb-0.5">{s.label}</div>
          <div className="font-semibold text-gray-900 leading-snug">{s.value}</div>
        </div>
      ))}
    </div>
  );
}

function PlanFeatures({ features }) {
  return (
    <div className="p-6 pt-4 flex-1">
      <ul className="space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-br-bronze mt-0.5 shrink-0" />
            <span className="text-gray-700">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * MaintenancePlanCard
 *
 * Each card shows the residential plan and exposes two CTAs:
 *  - Primary: "Choose <plan>" — calls `onChoose(plan)` so the parent page can
 *    scroll to the membership request form and prefill the selected plan.
 *  - Secondary text link: "See What's Included" — scrolls to the membership
 *    includes section so the homeowner can review what they're paying for.
 *
 * Falls back to an in-page scroll if `onChoose` is not provided.
 */
export default function MaintenancePlanCard({ plan, onChoose }) {
  const handleChoose = (e) => {
    e.preventDefault();
    if (typeof onChoose === "function") {
      onChoose(plan);
      return;
    }
    // Fallback: scroll to the request form anchor.
    const el = document.getElementById("request-details");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSeeIncluded = (e) => {
    e.preventDefault();
    const el = document.getElementById("what-included");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className={`relative rounded-lg overflow-hidden flex flex-col ${plan.highlight ? "border-2 border-br-bronze shadow-xl" : "border border-gray-200"} bg-white`}
      data-testid={`plan-card-${plan.id}`}
    >
      <PlanHeader plan={plan} />
      <PlanStats plan={plan} />
      <PlanFeatures features={plan.features} />
      <div className="p-6 pt-2 space-y-3">
        <button
          type="button"
          onClick={handleChoose}
          className={`${plan.highlight ? "br-btn-primary" : "br-btn-dark"} w-full justify-center`}
          data-testid={`plan-select-${plan.id}`}
        >
          {plan.cta} <ArrowRight size={14} />
        </button>
        <button
          type="button"
          onClick={handleSeeIncluded}
          className="block w-full text-center text-[11px] uppercase tracking-[0.25em] text-br-bronze hover:text-br-bronze-2 underline underline-offset-4 decoration-1"
          data-testid={`plan-see-included-${plan.id}`}
        >
          See What&rsquo;s Included
        </button>
      </div>
    </div>
  );
}
